import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime, map, merge } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { AuthService } from '../../../../../core/services/auth.service';
import { LanguageService } from '../../../../../core/services/language.service';
import { ButtonComponent } from '../../../../../osp-ui/components/button/button/button.component';
import { CardOptionComponent } from '../../../../../osp-ui/components/form/card-option-list/components/card-option/card-option.component';
import { DropdownComponent } from '../../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../osp-ui/components/form/form-field/form-field.component';
import { InputDateComponent } from '../../../../../osp-ui/components/form/input-date/input-date.component';
import { InputTextComponent } from '../../../../../osp-ui/components/form/input-text/input-text.component';
import { InputTextareaComponent } from '../../../../../osp-ui/components/form/input-textarea/input-textarea.component';
import { StepComponent } from '../../../../../osp-ui/components/panel/stepper/components/step/step.component';
import { StepContentDirective } from '../../../../../osp-ui/components/panel/stepper/directives/step-content.directive';
import { StepFooterDirective } from '../../../../../osp-ui/components/panel/stepper/directives/step-footer.directive';
import { StepperComponent } from '../../../../../osp-ui/components/panel/stepper/stepper.component';
import { MapSearchPlaceResult } from '../../../../../shared/components/maps/contract/map-search-place-result.contract';
import { MapLoaderService } from '../../../../../shared/components/maps/services/map-loader.service';
import { Address } from '../../../../../shared/contract/address.contract';
import { createDialogResult, DIALOG_ACTION, DialogResult } from '../../../../../shared/contract/dialog-result.contract';
import { Authorities } from '../../../../../shared/enums/authorities.enum';
import { IconPipe } from '../../../../../shared/pipes/icon.pipe';
import { AddressService } from '../../../../../shared/services/address.service';
import { OptionConverterService } from '../../../../../shared/services/option-converter.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { EquipmentsApiService } from '../../../../resources/equipment/services/equipments-api.service';
import { LocationAddressFormComponent } from '../../../shared/components/location-address-form/location-address-form.component';
import {
  hasAnyAddressPart,
  hasValidCoordinates,
  locationAddressOrCoordinatesValidator,
} from '../../../shared/validators/location-address.validator';
import { reverseGeocodeAddressAt } from '../../../shared/utils/reverse-geocode-address.util';
import { createGeocodeSyncGuard } from '../../../shared/utils/geocode-sync-guard';
import { ViewProject } from '../../contract/view-project.contract';
import { ProjectType } from '../../enums/project-type.enum';
import { ProjectApiService } from '../../services/project-api.service';
import { ProjectAddEditDialogFormController } from './services/project-add-edit-dialog-form-controller.service';

@Component({
  selector: 'osp-project-add-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputTextComponent,
    InputDateComponent,
    InputTextareaComponent,
    StepperComponent,
    StepComponent,
    StepContentDirective,
    StepFooterDirective,
    IconPipe,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
    DropdownComponent,
    CardOptionComponent,
    NgTemplateOutlet,
    LocationAddressFormComponent,
  ],
  providers: [ProjectAddEditDialogFormController],
  templateUrl: './project-add-edit-dialog.component.html',
  styleUrls: ['./project-add-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectAddEditDialogComponent {
  readonly formService = inject(ProjectAddEditDialogFormController);
  private readonly dialogRef = inject(DynamicDialogRef<DialogResult<string>>);
  private readonly dialogConfig = inject<DynamicDialogConfig<{ project?: ViewProject }>>(DynamicDialogConfig);
  private readonly projectApiService = inject(ProjectApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly languageService = inject(LanguageService);
  private readonly mapLoaderService = inject(MapLoaderService);
  private readonly addressService = inject(AddressService);
  private readonly optionConverterService = inject(OptionConverterService);
  private readonly equipmentsApiService = inject(EquipmentsApiService);
  private readonly toastService = inject(ToastService);
  private readonly ngZone = inject(NgZone);

  private readonly ORGANISATIONS_PAGE_SIZE = 5000;

  protected readonly isEditMode = !!this.dialogConfig.data?.project;
  protected readonly IconKey = IconKey;

  readonly form = this.formService.buildForm(this.dialogConfig.data?.project);

  readonly $organisations = toSignal(
    this.equipmentsApiService
      .getOrganisations(0, this.ORGANISATIONS_PAGE_SIZE)
      .pipe(map((res) => this.optionConverterService.convertOrganisationInfo(res.content))),
    { initialValue: [] },
  );
  readonly isMapLoaded$ = this.mapLoaderService.isLoaded;

  mapCenter: google.maps.LatLngLiteral = { lat: 51.1657, lng: 10.4515 };
  mapMarkerPosition: google.maps.LatLngLiteral | null = null;
  private isAddressGeocodePending = false;
  private addressNotResolved = false;
  private readonly geocodeSyncGuard = createGeocodeSyncGuard(environment.DELAY_SHORTEST);
  private readonly locationAddressValidator = locationAddressOrCoordinatesValidator(() => this.addressNotResolved);
  private readonly $projectType = signal<ProjectType | null | undefined>(
    this.form.controls.projectData.controls.projectType.value,
  );

  readonly $selectedOrganisation = computed(() => {
    const orgId = this.form.controls.projectData.controls.organisationId.value;
    return this.$organisations().find((o) => o.value === orgId)?.label || '-';
  });

  readonly projectTypeOptions = [
    { type: ProjectType.FIX, label: this.languageService.getInstant('modules.locations.category.project.fix') },
    { type: ProjectType.MOBIL, label: this.languageService.getInstant('modules.locations.category.project.mobil') },
  ];

  readonly locationInfoLabel = this.languageService.getInstant('modules.locations.project.create.infoBox.title');
  readonly locationInfoDescription = this.languageService.getInstant(
    'shared.createEntity.entitySelection.project.description',
  );

  readonly showAutomaticGeofence = !this.isEditMode && this.authService.hasAuthority(Authorities.GEOFENCE_MANAGE);

  constructor() {
    this.destroyRef.onDestroy(() => this.geocodeSyncGuard.dispose());

    const lat = this.form.controls.projectAddress.controls.lat.value;
    const lon = this.form.controls.projectAddress.controls.lon.value;
    if (hasValidCoordinates(lat, lon)) {
      this.setMapLocation(lat!, lon!);
    }

    this.form.controls.projectAddress.addValidators(this.locationAddressValidator);

    const projectAddress = this.form.controls.projectAddress;

    merge(
      projectAddress.controls.street.valueChanges,
      projectAddress.controls.streetNumber.valueChanges,
      projectAddress.controls.postalCode.valueChanges,
      projectAddress.controls.city.valueChanges,
    )
      .pipe(debounceTime(environment.DELAY_SHORTEST), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.geocodeSyncGuard.isReverseGeocodeUpdate) {
          return;
        }

        const value = projectAddress.getRawValue();
        const address = this.addressService.format(value);
        if (!address.length || !this.isGoogleMapsLoaded()) {
          this.isAddressGeocodePending = false;
          this.updateAddressResolvedError(false);
          return;
        }

        this.isAddressGeocodePending = true;
        new google.maps.Geocoder().geocode({ address }, (results, status) => {
          this.ngZone.run(() => {
            this.isAddressGeocodePending = false;
            if (status !== 'OK' || !results?.length) {
              this.updateAddressResolvedError(true);
              return;
            }

            const location = results[0].geometry?.location;
            if (!location) return;

            const latResult = location.lat();
            const lonResult = location.lng();
            this.geocodeSyncGuard.markForwardGeocodePatch();
            projectAddress.patchValue({ lat: latResult, lon: lonResult });
            this.updateAddressResolvedError(false);
            this.setMapLocation(latResult, lonResult);
          });
        });
      });

    merge(projectAddress.controls.lat.valueChanges, projectAddress.controls.lon.valueChanges)
      .pipe(debounceTime(environment.DELAY_SHORTEST), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const lat = projectAddress.controls.lat.value;
        const lon = projectAddress.controls.lon.value;
        if (!hasValidCoordinates(lat, lon)) {
          return;
        }

        if (this.geocodeSyncGuard.isForwardGeocodeUpdate) {
          this.setMapLocation(lat!, lon!);
          return;
        }

        this.setMapLocation(lat!, lon!);
        void this.reverseGeocodeAndPatchAddress(lat!, lon!);
      });

    this.updateAddressStepState(this.form.controls.projectData.controls.projectType.value);
    this.form.controls.projectData.controls.projectType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.$projectType.set(type);
        this.updateAddressStepState(type);
      });
  }

  handleMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lon = event.latLng.lng();
    this.updateAddressFromCoordinates(lat, lon);
  }

  handleSearchLocationChange({ lat, lon, street, streetNumber, postalCode, city }: MapSearchPlaceResult): void {
    if (hasAnyAddressPart({ street, streetNumber, postalCode, city })) {
      this.form.controls.projectAddress.patchValue(
        {
          city,
          lat,
          lon,
          postalCode,
          street,
          streetNumber,
        },
        { emitEvent: false },
      );
      this.setMapLocation(lat, lon);
      this.updateAddressResolvedError(false);
      return;
    }

    this.updateAddressFromCoordinates(lat, lon);
  }

  private updateAddressFromCoordinates(lat: number, lon: number): void {
    this.form.controls.projectAddress.patchValue({ lat, lon });
    this.setMapLocation(lat, lon);
  }

  private async reverseGeocodeAndPatchAddress(lat: number, lon: number): Promise<void> {
    const requestId = this.geocodeSyncGuard.beginReverseGeocodeRequest();
    const address = await reverseGeocodeAddressAt(lat, lon, (result) => this.extractAddress(result));

    if (this.geocodeSyncGuard.isStaleReverseGeocodeRequest(requestId)) {
      return;
    }

    this.ngZone.run(() => {
      if (!address) {
        this.updateAddressResolvedError(true);
        return;
      }

      this.geocodeSyncGuard.markReverseGeocodePatch();
      this.form.controls.projectAddress.patchValue({
        street: address.street,
        streetNumber: address.streetNumber,
        postalCode: address.postalCode,
        city: address.city,
      });
      this.updateAddressResolvedError(false);
    });
  }

  onCancel(): void {
    this.dialogRef.close(createDialogResult(DIALOG_ACTION.GO_BACK));
  }

  protected readonly goBackToItemSelection = (): void => {
    this.dialogRef.close(createDialogResult(DIALOG_ACTION.GO_BACK));
  };

  handleSubmit(): void {
    if (
      this.form.invalid ||
      this.form.pending ||
      (this.isAddressGeocodePending && !this.hasResolvableLocationInput())
    ) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode) {
      this.handleUpdate();
    } else {
      this.handleCreate();
    }
  }

  private handleCreate(): void {
    const command = this.formService.getCreateCommand();
    this.projectApiService
      .createProject(command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toastService.showSuccess({
          summary: this.languageService.getInstant('modules.locations.project.create.toast.success'),
        });
        this.dialogRef.close(createDialogResult(DIALOG_ACTION.SAVE));
        this.router.navigate(['/locations/active']);
      });
  }

  private handleUpdate(): void {
    const command = this.formService.getUpdateCommand();
    this.projectApiService
      .updateProject(command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toastService.showSuccess({
          summary: this.languageService.getInstant('modules.locations.project.edit.toast.success'),
        });
        this.dialogRef.close(createDialogResult(DIALOG_ACTION.SAVE));
        this.router.navigate(['/locations/active']);
      });
  }

  private setMapLocation(lat: number, lng: number): void {
    this.mapCenter = { lat, lng };
    this.mapMarkerPosition = { lat, lng };
  }

  private isGoogleMapsLoaded(): boolean {
    return typeof google !== 'undefined' && !!google.maps;
  }

  protected get isSubmitDisabled(): boolean {
    return (
      this.form.invalid || this.form.pending || (this.isAddressGeocodePending && !this.hasResolvableLocationInput())
    );
  }

  private hasResolvableLocationInput(): boolean {
    const address = this.form.controls.projectAddress.getRawValue();
    return hasValidCoordinates(address.lat, address.lon) || hasAnyAddressPart(address);
  }

  private updateAddressStepState(type: ProjectType | null | undefined): void {
    if (type) {
      this.form.controls.projectAddress.enable({ emitEvent: false });
      this.form.controls.projectAddress.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.form.controls.projectAddress.disable({ emitEvent: false });
    this.updateAddressResolvedError(false);
  }

  private updateAddressResolvedError(isNotResolved: boolean): void {
    this.addressNotResolved = isNotResolved;
    this.form.controls.projectAddress.updateValueAndValidity();
  }

  private extractAddress(result: google.maps.GeocoderResult): Address {
    return {
      street: this.getAddressPart(result, 'route'),
      streetNumber: this.getAddressPart(result, 'street_number'),
      postalCode: this.getAddressPart(result, 'postal_code'),
      city:
        this.getAddressPart(result, 'locality') ||
        this.getAddressPart(result, 'postal_town') ||
        this.getAddressPart(result, 'administrative_area_level_2'),
    };
  }

  private getAddressPart(result: google.maps.GeocoderResult, type: string, useShortName = false): string {
    const part = result.address_components.find((component) => component.types.includes(type));
    return (useShortName ? part?.short_name : part?.long_name) || '';
  }
}
